import type { CSSProperties } from 'react';
import {
  DRUM_COLORS,
  DRUM_IDS,
  DRUM_LABELS,
  DRUM_STEPS_PER_MEASURE,
  type DrumId,
  type DrumPattern,
} from '../types';
import { previewDrumSample } from '../audio/drumSamples';

interface Props {
  pattern: DrumPattern;
  activeStep: number | null;
  onToggleStep: (drum: DrumId, stepIndex: number) => void;
  onClearRow: (drum: DrumId) => void;
}

export function DrumStepGrid({
  pattern,
  activeStep,
  onToggleStep,
  onClearRow,
}: Props) {
  const totalSteps = pattern.measures * DRUM_STEPS_PER_MEASURE;

  return (
    <div className="drum-step-grid" role="grid" aria-label="Patrón de batería">
      <div className="drum-step-grid__ruler" aria-hidden="true">
        <span className="drum-step-grid__corner" />
        <div className="drum-step-grid__ruler-track">
          {Array.from({ length: totalSteps }, (_, step) => {
            const barStart = step % DRUM_STEPS_PER_MEASURE === 0;
            const beatStart = step % 4 === 0;
            const beatInBar = Math.floor((step % DRUM_STEPS_PER_MEASURE) / 4) + 1;
            return (
              <span
                key={step}
                className={[
                  'drum-step-grid__ruler-cell',
                  barStart ? 'drum-step-grid__ruler-cell--bar' : '',
                  beatStart ? 'drum-step-grid__ruler-cell--beat' : '',
                ]
                  .filter(Boolean)
                  .join(' ')}
              >
                {barStart ? `C${Math.floor(step / DRUM_STEPS_PER_MEASURE) + 1}` : beatStart ? beatInBar : ''}
              </span>
            );
          })}
        </div>
      </div>

      {DRUM_IDS.map((drum) => (
        <div key={drum} className="drum-step-grid__row" role="row">
          <div className="drum-step-grid__track" role="rowheader">
            <button
              type="button"
              className="drum-step-grid__track-preview"
              style={{ '--drum-color': DRUM_COLORS[drum] } as CSSProperties}
              onClick={() => previewDrumSample(drum)}
              title={`Escuchar ${DRUM_LABELS[drum]}`}
            >
              <span className="drum-step-grid__track-dot" aria-hidden="true" />
              {DRUM_LABELS[drum]}
            </button>
            <button
              type="button"
              className="drum-step-grid__clear"
              onClick={() => onClearRow(drum)}
              title={`Limpiar ${DRUM_LABELS[drum]}`}
            >
              ×
            </button>
          </div>

          <div className="drum-step-grid__steps">
            {Array.from({ length: totalSteps }, (_, step) => {
              const on = pattern.steps[drum][step];
              const beatStart = step % 4 === 0;
              const barStart = step % DRUM_STEPS_PER_MEASURE === 0;
              const isActive = activeStep === step;

              return (
                <button
                  key={step}
                  type="button"
                  role="gridcell"
                  className={[
                    'drum-step-grid__cell',
                    on ? 'drum-step-grid__cell--on' : '',
                    beatStart ? 'drum-step-grid__cell--beat' : '',
                    barStart ? 'drum-step-grid__cell--bar' : '',
                    isActive ? 'drum-step-grid__cell--active' : '',
                  ]
                    .filter(Boolean)
                    .join(' ')}
                  style={{ '--drum-color': DRUM_COLORS[drum] } as CSSProperties}
                  aria-pressed={on}
                  aria-label={`${DRUM_LABELS[drum]}, paso ${step + 1}`}
                  onClick={() => onToggleStep(drum, step)}
                />
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}
